import { Check, Eye, X, Lock } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { PERMISSIONS, ROLES, ROLE_LABELS, type Role, type Module } from '@/types';

const MODULES: { key: Module; label: string; group: string }[] = [
  { key: 'dashboard',  label: 'Dashboard',   group: 'Operate' },
  { key: 'clients',    label: 'Clients',     group: 'Operate' },
  { key: 'quotes',     label: 'Quotes',      group: 'Operate' },
  { key: 'orders',     label: 'Orders',      group: 'Operate' },
  { key: 'production', label: 'Production',  group: 'Operate' },
  { key: 'inventory',  label: 'Inventory',   group: 'Operate' },
  { key: 'financials', label: 'Financials',  group: 'Finance' },
  { key: 'accounting', label: 'Accounting',  group: 'Finance' },
  { key: 'reports',    label: 'Reports',     group: 'Finance' },
  { key: 'hr',         label: 'People',      group: 'Admin' },
  { key: 'settings',   label: 'Settings',    group: 'Admin' },
];

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner:      'Unrestricted access. Provisions users, edits settings. Always at least one required.',
  manager:    'Full operational access. Read-only on financials, accounting, HR.',
  accounting: 'Money side: AR, AP, GL, close. Read-only on operations.',
  sales:      'CRM, quoting, orders. Reads inventory + production.',
  production: 'Shop floor + inventory. Reads orders + clients.',
  staff:      'Dashboard read-only. Minimal footprint.',
};

export function RolesSettings() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="kicker mb-2">Permissions · Read-only</div>
        <h2 className="font-display text-2xl text-text mb-3">Role Matrix</h2>
        <p className="text-sm text-text-muted max-w-2xl leading-relaxed">
          Each role grants a fixed set of read or write permissions across the portal's modules.
          The same matrix is enforced in the React app, the API, and the Firestore security rules — defense in depth.
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 text-xs">
        <LegendItem icon={Check} label="Read & Write" tone="brass" />
        <LegendItem icon={Eye}   label="Read-Only"   tone="info" />
        <LegendItem icon={X}     label="No Access"   tone="dim" />
      </div>

      {/* Matrix */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="kicker px-6 py-4 font-normal text-left bg-bg-subtle">Module</th>
                {ROLES.map((r) => (
                  <th key={r} className="kicker px-3 py-4 font-normal text-center bg-bg-subtle min-w-[100px]">
                    {ROLE_LABELS[r]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODULES.map((m, i) => {
                const showGroup = i === 0 || MODULES[i - 1].group !== m.group;
                return (
                  <tr key={m.key} className="border-b border-border last:border-0">
                    <td className="px-6 py-3 align-middle">
                      <div className="flex items-baseline gap-3">
                        {showGroup && (
                          <span className="kicker text-brass-300 w-16 shrink-0">{m.group}</span>
                        )}
                        {!showGroup && <span className="w-16 shrink-0" />}
                        <span className="text-text">{m.label}</span>
                      </div>
                    </td>
                    {ROLES.map((r) => (
                      <td key={r} className="px-3 py-3 text-center">
                        <PermCell perm={PERMISSIONS[r][m.key]} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Role descriptions */}
      <div>
        <h3 className="font-display text-lg text-text mb-4 flex items-baseline gap-3">
          <span>Role Descriptions</span>
          <Badge tone="neutral">{ROLES.length}</Badge>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ROLES.map((r) => (
            <div
              key={r}
              className="bg-bg-panel border border-border rounded-sm p-5 hover:border-border-strong transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <Badge tone={r === 'owner' ? 'brass' : r === 'staff' ? 'neutral' : 'info'}>
                  {ROLE_LABELS[r]}
                </Badge>
              </div>
              <p className="text-sm text-text-muted leading-relaxed">{ROLE_DESCRIPTIONS[r]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Lock notice */}
      <div className="flex items-start gap-3 p-4 bg-bg-subtle border border-border rounded-sm">
        <Lock size={14} className="text-text-dim shrink-0 mt-0.5" />
        <p className="text-xs text-text-muted leading-relaxed">
          The role matrix is hard-coded in the application source for safety. Changing it requires a code change
          across <span className="font-mono text-brass-300">frontend/src/types/index.ts</span>,{' '}
          <span className="font-mono text-brass-300">backend/functions/src/config/permissions.ts</span>, and{' '}
          <span className="font-mono text-brass-300">infrastructure/firestore.rules</span> — all in the same deploy.
          See <span className="font-mono text-brass-300">docs/ROLES.md</span>.
        </p>
      </div>
    </div>
  );
}

function PermCell({ perm }: { perm: 'rw' | 'r' | false }) {
  if (perm === 'rw') {
    return (
      <div className="inline-flex h-7 w-7 rounded-sm bg-brass-400/15 border border-brass-400/40 items-center justify-center" title="Read & Write">
        <Check size={13} className="text-brass-300" strokeWidth={2.5} />
      </div>
    );
  }
  if (perm === 'r') {
    return (
      <div className="inline-flex h-7 w-7 rounded-sm bg-info/10 border border-info/30 items-center justify-center" title="Read only">
        <Eye size={12} className="text-info" />
      </div>
    );
  }
  return (
    <div className="inline-flex h-7 w-7 rounded-sm bg-bg-subtle border border-border items-center justify-center" title="No access">
      <X size={12} className="text-text-dim" />
    </div>
  );
}

function LegendItem({ icon: Icon, label, tone }: { icon: typeof Check; label: string; tone: 'brass' | 'info' | 'dim' }) {
  const styles = {
    brass: 'bg-brass-400/15 border-brass-400/40 text-brass-300',
    info:  'bg-info/10 border-info/30 text-info',
    dim:   'bg-bg-subtle border-border text-text-dim',
  }[tone];
  return (
    <div className="flex items-center gap-2">
      <div className={`inline-flex h-5 w-5 rounded-sm border items-center justify-center ${styles}`}>
        <Icon size={10} strokeWidth={2.5} />
      </div>
      <span className="text-text-muted">{label}</span>
    </div>
  );
}
